import compact from 'lodash/compact';
import { FC } from 'react';

import { ArticleAudio } from 'editorial/components/ArticleAudio';
import { ArticleCredits } from 'editorial/components/ArticleCredits';
import { ArticleFullImage } from 'editorial/components/ArticleFullImage';
import { ArticleFullVideo } from 'editorial/components/ArticleFullVideo';
import { ArticleImageSet } from 'editorial/components/ArticleImageSet';
import { ArticleIntroduction } from 'editorial/components/ArticleIntroduction';
import { ArticleQuestionAnswer } from 'editorial/components/ArticleQuestionAnswer';
import { ArticleQuote } from 'editorial/components/ArticleQuote';
import { ArticleSpace } from 'editorial/components/ArticleSpace';
import { ArticleTextSection } from 'editorial/components/ArticleTextSection';
import { ArticleTopShoes } from 'editorial/components/ArticleTopShoes';
import { EDITORIAL_SECTION_TYPE } from 'editorial/enums/constants';
import { FeatureFlags } from 'featureFlags/constants';
import { IArticleSection, IArticleSectionAdditionalProps } from 'editorial/types';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';

const editorialSectionComponents = Object.freeze({
  [EDITORIAL_SECTION_TYPE.AUDIO]: ArticleAudio,
  [EDITORIAL_SECTION_TYPE.AUTHOR_CREDIT]: ArticleCredits,
  [EDITORIAL_SECTION_TYPE.FULL_IMAGE]: ArticleFullImage,
  [EDITORIAL_SECTION_TYPE.IMAGE_SET]: ArticleImageSet,
  [EDITORIAL_SECTION_TYPE.INTRODUCTION]: ArticleIntroduction,
  [EDITORIAL_SECTION_TYPE.QA]: ArticleQuestionAnswer,
  [EDITORIAL_SECTION_TYPE.QUOTE]: ArticleQuote,
  [EDITORIAL_SECTION_TYPE.TEXT]: ArticleTextSection,
  [EDITORIAL_SECTION_TYPE.TOP_SHOES]: ArticleTopShoes,
  [EDITORIAL_SECTION_TYPE.VIDEO_HERO]: ArticleFullVideo,
  [EDITORIAL_SECTION_TYPE.SPACE]: ArticleSpace,
});

interface IArticleContentIterator {
  meta: { subject: string; author: string };
  category: string;
  sections: Array<IArticleSection>;
}

export const ArticleContentIterator = ({
  category,
  meta,
  sections = [],
}: IArticleContentIterator) => {
  const enableEditorialSpace = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_EDITORIAL_SPACE);

  return (
    <>
      {compact([...sections]).map((section, i) => {
        const { type, ...sectionProps } = section;
        // only show initials when the previous section was not also a Q&A section
        const isInitialsVisible =
          type === EDITORIAL_SECTION_TYPE.QA &&
          sections?.[i - 1]?.type !== EDITORIAL_SECTION_TYPE.QA;
        const EditorialSectionComponent: FC<IArticleSection & IArticleSectionAdditionalProps> =
          editorialSectionComponents[type];

        if (!EditorialSectionComponent) return null;

        if (!enableEditorialSpace && type === EDITORIAL_SECTION_TYPE.SPACE) return null;

        return (
          <EditorialSectionComponent
            key={section?.uuid || section?.id}
            category={category}
            isInitialsVisible={isInitialsVisible}
            {...meta}
            {...sectionProps}
          />
        );
      })}
    </>
  );
};
